import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Container, Row, Alert, Form } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Logout } from '../../../../store/actions/AuthActions';
import { useManagerSubscription } from '../../../../context/ManagerSubscriptionContext';
import ManagerSubscriptionsApiClient from '../../../../api-client/ManagerSubscriptionsApiClient';
import { store } from '../../../../store/store';

const PAYMENT_METHODS = [
    { value: 'card', label: 'Carta di credito', help: 'Rinnovo automatico ogni anno, senza pensieri.' },
    { value: 'paypal', label: 'PayPal', help: 'Rinnovo automatico ogni anno, senza pensieri.' },
    { value: 'bank_transfer', label: 'Bonifico bancario', help: "Riceverai le coordinate per il bonifico. L'abbonamento si attiva a pagamento ricevuto (1–3 giorni lavorativi) e te lo confermiamo via email. Rinnovo manuale." },
];

const AbbonamentoPage = () => {
    const { subscription } = useManagerSubscription();
    const [discountCode, setDiscountCode] = useState('');
    const [discountInfo, setDiscountInfo] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [platformPrice, setPlatformPrice] = useState(149.00);
    const [paymentMethod, setPaymentMethod] = useState('card');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const token = store.getState().auth.auth.idToken;
                const apiClient = new ManagerSubscriptionsApiClient();
                const res = await apiClient.getAdminSettings(token);
                if (res && res.listPriceEurCents !== undefined) {
                    setPlatformPrice(res.listPriceEurCents / 100);
                }
            } catch (e) {
                console.error("Errore nel recupero dei prezzi della piattaforma:", e);
            }
        };
        fetchSettings();
    }, []);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleLogout = () => {
        dispatch(Logout(navigate));
    };

    // Se l'abbonamento è ancora attivo (es. prova gratuita in corso) l'utente è
    // arrivato qui volontariamente dal banner: può tornare indietro. Se invece è
    // scaduto/inattivo è stato reindirizzato forzatamente e l'unica uscita è il logout.
    const cameVoluntarily = subscription?.active === true;

    const handleBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate('/struttura/dashboard');
    };

    // Se è una trial gratuita (priceEur === 0) la pagina sta proponendo l'attivazione,
    // quindi mostro il prezzo di listino, non lo 0 del trial.
    const isTrial = subscription?.priceEur === 0;
    const price = subscription?.priceEur !== undefined && !isTrial ? subscription.priceEur : platformPrice;

    const handleRemoveDiscount = () => {
        setDiscountCode('');
        setDiscountInfo(null);
        setError('');
    };

    const handleVerifyDiscount = async () => {
        if (!discountCode) return;
        setLoading(true);
        setError('');
        try {
            const token = store.getState().auth.auth.idToken;
            const apiClient = new ManagerSubscriptionsApiClient();
            const res = await apiClient.verifyDiscount(token, discountCode);
            if (res.error) {
                setError(res.error);
                setDiscountInfo(null);
            } else if (!res.valid) {
                setError('Codice sconto non valido');
                setDiscountInfo(null);
            } else {
                setDiscountInfo(res);
            }
        } catch (e) {
            setError('Errore durante la verifica del codice sconto');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckout = async () => {
        setLoading(true);
        setError('');
        try {
            const token = store.getState().auth.auth.idToken;
            const apiClient = new ManagerSubscriptionsApiClient();
            const res = await apiClient.checkout(token, discountInfo ? discountCode : null, paymentMethod);
            if (res.error) {
                if (res.error === 'Conflict') {
                    setError('Hai già un abbonamento attivo.');
                } else if (res.error === 'PendingBankTransfer') {
                    setError('Hai un bonifico in attesa di accredito. Completalo con le coordinate ricevute (o attendi la registrazione del pagamento) prima di avviare un nuovo pagamento.');
                } else {
                    setError(res.error);
                }
            } else if (res.checkoutUrl) {
                window.location.href = res.checkoutUrl;
            }
        } catch (e) {
            setError('Errore durante l\'avvio del checkout');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container fluid className="h-100 mt-3 pt-3">
            <Row className="justify-content-center align-items-center h-100">
                <Col md={6}>
                    <Card className="shadow-sm">
                        <Card.Body className="p-5 text-center">
                            <h2 className="mb-4">Adesso passiamo alle cose formali</h2>
                            <p className="mb-4">
                                Con l’attivazione dell’abbonamento potrai sfruttare 2 ore di assistenza tecnica per <br></br>configurare al meglio il tuo <b>SportId Manager®</b>
                            </p>

                            <div className="mb-4">
                                <h3>{discountInfo ? `€ ${discountInfo.discountedPriceEur.toFixed(2)}` : `€ ${price.toFixed(2)}`} / anno</h3>
                                {discountInfo && <p className="text-success mb-1">Sconto applicato! (valido solo per il primo anno — dal secondo anno verrà addebitato il prezzo pieno di € {price.toFixed(2)})</p>}
                                {/*<p className="small mb-0">Il rinnovo è automatico e senza pensieri, garantendoti continuità nel servizio e nel risparmio.<br></br>
                                    Non perdere questa opportunità, questa offerta è limitata.</p>*/}
                            </div>

                            <Form.Group className="mb-4 text-start">
                                <Form.Label>Codice Sconto (opzionale)</Form.Label>
                                <div className="d-flex" style={{ gap: 8 }}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Inserisci codice"
                                        value={discountCode}
                                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                                        disabled={loading || discountInfo}
                                        style={{ height: 56 }}
                                    />
                                    {!discountInfo ? (
                                        <Button
                                            variant="outline-primary"
                                            style={{ whiteSpace: 'nowrap', height: 56 }}
                                            onClick={handleVerifyDiscount}
                                            disabled={loading || !discountCode}
                                        >
                                            Verifica
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outline-danger"
                                            style={{ height: 56 }}
                                            onClick={handleRemoveDiscount}
                                            disabled={loading}
                                        >
                                            Rimuovi
                                        </Button>
                                    )}
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-4 text-start">
                                <Form.Label>Metodo di pagamento</Form.Label>
                                <div className="d-flex flex-column" style={{ gap: 8 }}>
                                    {PAYMENT_METHODS.map((m) => (
                                        <Form.Check
                                            key={m.value}
                                            type="radio"
                                            name="paymentMethod"
                                            id={`pm-${m.value}`}
                                            checked={paymentMethod === m.value}
                                            onChange={() => setPaymentMethod(m.value)}
                                            disabled={loading}
                                            label={
                                                <span>
                                                    <b>{m.label}</b>
                                                    <br />
                                                    <small className="text-muted">{m.help}</small>
                                                </span>
                                            }
                                        />
                                    ))}
                                </div>
                            </Form.Group>

                            {error && <Alert variant="danger">{error}</Alert>}

                            <Button
                                variant="primary"
                                size="lg"
                                className="w-100 mt-3"
                                onClick={handleCheckout}
                                disabled={loading || (subscription && subscription.active && !isTrial)}
                            >
                                {loading ? 'Caricamento...' : 'Procedi al Pagamento'}
                            </Button>

                            {cameVoluntarily ? (
                                <Button
                                    variant="outline-secondary"
                                    size="lg"
                                    className="w-100 mt-3"
                                    onClick={handleBack}
                                    disabled={loading}
                                >
                                    Indietro
                                </Button>
                            ) : (
                                <Button
                                    variant="outline-secondary"
                                    size="lg"
                                    className="w-100 mt-3"
                                    onClick={handleLogout}
                                    disabled={loading}
                                >
                                    Esci (Logout)
                                </Button>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AbbonamentoPage;